import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useMemo, useState } from 'preact/hooks'
import type { Position } from '../api/generated/Position'
import type { ShiftSeries } from '../api/generated/ShiftSeries'
import { useAction } from '../api/useAction'
import { type Slicing, SlicingField, toSplit } from '../shifts/SlicingField'
import { addMinutes, formatDay, formatRange } from '../shifts/time'
import { PositionSelect } from './PositionSelect'
import { type DayRange, MAX_SERIES_SHIFTS, seriesShifts } from './series'

const inputValue = (e: Event) => (e.target as HTMLInputElement).value
const shrink = { inputLabel: { shrink: true } }

type Hours = Omit<DayRange, 'day'>

const dayAfter = (day: string, days: number) =>
  addMinutes(`${day}T00:00:00`, days * 1440).slice(0, 10)

/** Creates consecutive shifts of a position over one or more days, after a preview. */
export function SeriesDialog({
  positions,
  initialPositionId,
  days,
  initialDay,
  onSubmit,
  onClose,
}: {
  positions: Position[]
  initialPositionId?: string
  days: string[]
  initialDay: string
  onSubmit: (series: ShiftSeries) => Promise<void>
  onClose: () => void
}) {
  const [positionId, setPositionId] = useState(initialPositionId ?? positions[0]?.id ?? '')
  // A day keeps its hours once unchecked; a newly checked day takes the last hours entered
  const [hours, setHours] = useState<Record<string, Hours>>({
    [initialDay]: { start: '08:00', end: '18:00' },
  })
  const [lastHours, setLastHours] = useState<Hours>({ start: '08:00', end: '18:00' })
  const [shownDays, setShownDays] = useState(days)
  const [chosenDays, setChosenDays] = useState<ReadonlySet<string>>(new Set([initialDay]))
  const [slicing, setSlicing] = useState<Slicing>({ by: 'minutes', amount: '120' })
  const [required, setRequired] = useState('1')
  const { error, pending, run } = useAction()
  const shifts = useMemo(
    () =>
      seriesShifts({
        ranges: [...chosenDays].flatMap((day) => {
          const range = hours[day]
          return range ? [{ day, ...range }] : []
        }),
        split: toSplit(slicing),
        required: Number(required),
      }),
    [chosenDays, hours, slicing, required],
  )
  const tooMany = shifts.length > MAX_SERIES_SHIFTS
  const count = `${shifts.length} créneau${shifts.length > 1 ? 'x' : ''}`

  const toggleDay = (day: string) => {
    setHours((current) => (current[day] ? current : { ...current, [day]: lastHours }))
    setChosenDays((current) => {
      const next = new Set(current)
      if (!next.delete(day)) next.add(day)
      return next
    })
  }
  /** Shows and checks the day before the first one, or after the last one: setup, teardown. */
  const addDay = (side: 'before' | 'after') => {
    const edge = side === 'before' ? shownDays[0] : shownDays.at(-1)
    if (!edge) return
    const day = dayAfter(edge, side === 'before' ? -1 : 1)
    setShownDays(side === 'before' ? [day, ...shownDays] : [...shownDays, day])
    toggleDay(day)
  }
  const setDayHours = (day: string, changed: Partial<Hours>) => {
    const updated = { ...(hours[day] ?? lastHours), ...changed }
    setHours({ ...hours, [day]: updated })
    setLastHours(updated)
  }
  const submit = (e: Event) => {
    e.preventDefault()
    run(() => onSubmit({ position_id: positionId, shifts }))
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>Série de créneaux</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {positions.length > 1 && (
              <PositionSelect positions={positions} value={positionId} onChange={setPositionId} />
            )}
            <FormControl component="fieldset">
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Jours et horaires
              </FormLabel>
              <Stack spacing={1}>
                {shownDays.map((day) => {
                  const range = hours[day]
                  return (
                    <Stack
                      key={day}
                      direction="row"
                      sx={{ alignItems: 'flex-start', flexWrap: 'wrap', columnGap: 2, rowGap: 1 }}
                    >
                      <FormControlLabel
                        label={formatDay(day)}
                        sx={{ minWidth: 140, mt: 0.5 }}
                        control={
                          <Checkbox checked={chosenDays.has(day)} onChange={() => toggleDay(day)} />
                        }
                      />
                      {chosenDays.has(day) && range && (
                        <Stack direction="row" spacing={1} sx={{ flex: 1, minWidth: 220 }}>
                          <TextField
                            label="De"
                            type="time"
                            required
                            size="small"
                            sx={{ flex: 1 }}
                            slotProps={shrink}
                            value={range.start}
                            onInput={(e) => setDayHours(day, { start: inputValue(e) })}
                          />
                          <TextField
                            label="À"
                            type="time"
                            required
                            size="small"
                            sx={{ flex: 1 }}
                            slotProps={shrink}
                            helperText={
                              range.end && range.end < range.start ? 'Le lendemain' : undefined
                            }
                            value={range.end}
                            onInput={(e) => setDayHours(day, { end: inputValue(e) })}
                          />
                        </Stack>
                      )}
                    </Stack>
                  )
                })}
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  <Button size="small" onClick={() => addDay('before')}>
                    Ajouter la veille
                  </Button>
                  <Button size="small" onClick={() => addDay('after')}>
                    Ajouter le lendemain
                  </Button>
                </Stack>
              </Stack>
            </FormControl>
            <SlicingField value={slicing} partsLabel="Créneaux par jour" onChange={setSlicing} />
            <TextField
              label="Bénévoles par créneau"
              type="number"
              required
              slotProps={{ htmlInput: { min: 1, max: 1000 } }}
              value={required}
              onInput={(e) => setRequired(inputValue(e))}
            />
            <Typography variant="subtitle2">
              {shifts.length === 0 ? 'Aucun créneau' : count}
            </Typography>
            {tooMany && (
              <Alert severity="warning">
                {MAX_SERIES_SHIFTS} créneaux au plus par série : allongez les créneaux ou choisissez
                moins de jours.
              </Alert>
            )}
            {shifts.length > 0 && !tooMany && (
              <List dense disablePadding sx={{ maxHeight: 200, overflowY: 'auto' }}>
                {shifts.map((shift) => (
                  <ListItem key={shift.starts_at} disableGutters>
                    <Typography variant="body2">
                      {formatRange(shift.starts_at, shift.ends_at)}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Annuler</Button>
          <Button
            type="submit"
            variant="contained"
            loading={pending}
            disabled={shifts.length === 0 || tooMany}
          >
            {shifts.length > 0 && !tooMany ? `Créer ${count}` : 'Créer'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
